/**
 * Sentry Error Tracking
 * Client-side integration for production error monitoring
 * Bundled from @sentry/browser (npm) — no CDN script tag involved.
 */

import * as Sentry from '@sentry/browser';

let initialized = false;

/**
 * Initialize Sentry for client-side error tracking
 * Only initializes in production (not localhost or local IPs)
 */
export function initSentry() {
    if (initialized) return;

    // Skip initialization in development
    if (typeof window === 'undefined') return;

    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (isLocalhost) {
        return;
    }

    // Get DSN from meta tag or global config
    const dsn = document.querySelector('meta[name="sentry-dsn"]')?.content;

    if (!dsn) {
        console.warn('Sentry DSN not configured');
        return;
    }

    const isPreview = hostname.includes('netlify.app');

    Sentry.init({
        dsn,
        environment: isPreview ? 'preview' : 'production',
        // Only send errors, not performance data
        tracesSampleRate: 0,

        // Filter out bot traffic before sending to Sentry
        beforeSend(event) {
            const userAgent = navigator.userAgent || '';

            // Filter HeadlessChrome and known bot patterns
            // These generate noise but aren't actionable issues
            if (userAgent.includes('HeadlessChrome') ||
                userAgent.includes('PhantomJS') ||
                userAgent.includes('Lighthouse') ||
                userAgent.includes('Googlebot') ||
                userAgent.includes('bingbot') ||
                userAgent.includes('Slackbot') ||
                userAgent.includes('facebookexternalhit')) {
                return null; // Don't send to Sentry
            }

            return event;
        },

        // Ignore common non-actionable errors
        ignoreErrors: [
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            'Non-Error promise rejection captured',
            /^Script error\.?$/,
            // Cloudflare Turnstile CAPTCHA errors (client-side, not fixable)
            'TurnstileError',
            /\[Cloudflare Turnstile\]/,
        ],
    });

    initialized = true;
}

/**
 * Capture an error manually
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export function captureError(error, context = {}) {
    if (!initialized) return;

    Sentry.withScope((scope) => {
        if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        if (context.extra) {
            Object.entries(context.extra).forEach(([key, value]) => {
                scope.setExtra(key, value);
            });
        }
        Sentry.captureException(error);
    });
}

/**
 * Capture a message (non-error event)
 * @param {string} message - The message to capture
 * @param {string} level - Severity level (info, warning, error)
 * @param {Object} context - Optional { tags, extra } attached to the event
 */
export function captureMessage(message, level = 'info', context = {}) {
    if (!initialized) return;

    Sentry.withScope((scope) => {
        if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        if (context.extra) {
            Object.entries(context.extra).forEach(([key, value]) => {
                scope.setExtra(key, value);
            });
        }
        Sentry.captureMessage(message, level);
    });
}
